import { useEffect, useState } from "react";
import { ProfileTypes } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/ProfileBaseballCard.utils";
import { kLevelID, kHeadcount, kPeersID } from "@coreapp/constants";
import {
  getDefaultEmployeeFieldConfig,
  getDefaultPositionFieldConfig,
  PROFILE_TEST_PREFIX
} from "./ProfileConfiguration.utils";
import {
  EmployeeBaseballCard,
  FavoriteManagerInterface
} from "@/appComponents/Blocks/Cards/ProfileBaseballCard/EmployeeBaseballCard";
import { PositionBaseballCard } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/PositionBaseballCard";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { BigInfoIcon, DialogContent, DialogTitle, Modal, Text } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { AgentBaseballCard } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/Agent/AgentBaseballCard";
import {
  getAgentSettingsFromHeadingConfig,
  getAvailableAgentSlots
} from "@/appComponents/Blocks/Cards/ProfileBaseballCard/Agent/AgentProfile.constants";

type PreviewModalProps = {
  modalElement: {
    record: Nullable<Record<string, any>>;
    type: ProfileTypes;
    mapping: any;
  };
  onClose: () => void;
  isPositionChart: boolean;
  chartConfig: any;
  fields: {
    [ProfileTypes.Employee]: string[];
    [ProfileTypes.Position]: string[];
    // Agent previews use a sample record; the field list gates availability.
    [ProfileTypes.Agent]?: string[];
  };
};

const getStarFields = (chartConfig: any, modalElement: any) => {
  const headingConfig = chartConfig?.profile?.headingConfig?.[modalElement?.type];
  let starFields: string[] = [];

  if (headingConfig) {
    starFields = [
      headingConfig.favoriteField1,
      headingConfig.favoriteField2,
      headingConfig.favoriteField3
    ];
  } else {
    // TODO: Change it to holder fields (when they exist).
    starFields =
      chartConfig?.profile?.starFields ||
      (IsPositionChart(chartConfig) ? [] : [kLevelID, kHeadcount, kPeersID]);
  }

  return starFields.filter(Boolean);
};

export const ProfilePreviewModal = ({
  modalElement,
  onClose,
  isPositionChart,
  chartConfig,
  fields
}: PreviewModalProps) => {
  const [showMoreFields, setShowMoreFields] = useState<boolean | null>(null);
  // The modal stays mounted (only `show` toggles), so reset the expansion
  // state on close — each new preview starts from its type's default
  // (agent: expanded via `?? true`).
  useEffect(() => {
    if (modalElement == null) {
      setShowMoreFields(null);
    }
  }, [modalElement]);
  const isPositionElement = modalElement?.type === ProfileTypes.Position;
  const isHolder = isPositionChart && !isPositionElement;
  const defaultConfig = {
    [ProfileTypes.Employee]: getDefaultEmployeeFieldConfig(
      isPositionChart,
      chartConfig?.fields || {}
    ),
    [ProfileTypes.Position]: isPositionChart
      ? getDefaultPositionFieldConfig(chartConfig?.fields || {})
      : null
  };

  const headingConfig = chartConfig?.profile?.headingConfig || defaultConfig;
  const agentSettings = getAgentSettingsFromHeadingConfig(chartConfig?.profile?.headingConfig);
  // Preview gates availability with the same field pool as the settings section.
  const agentFieldPool = fields?.[ProfileTypes.Agent];
  const agentFieldSet = agentFieldPool ? new Set(agentFieldPool) : null;
  const agentAvailableSlots = agentFieldSet
    ? getAvailableAgentSlots(fieldName => agentFieldSet.has(fieldName), agentSettings.fields)
    : null;
  const starFields: string[] = isPositionElement ? [] : getStarFields(chartConfig, modalElement);
  const favoriteOptions: FavoriteManagerInterface = {
    isStarringEnabled: false,
    getCurrentFavorites: () => starFields,
    toggleStarredField: (field: string) => {},
    removeStarredField: (field: { field: string; value: string }) => {}
  };

  const getVisibleFields = (type: ProfileTypes) => {
    const currentConfig = chartConfig?.profile?.shownFields?.[modalElement?.type]?.Included;
    const legacyConfig =
      type === ProfileTypes.Employee
        ? chartConfig?.profile?.shownFields
        : chartConfig?.profile?.shownPositionFields;

    return currentConfig || legacyConfig || fields?.[type];
  };

  return (
    <CentralizedModalContainer show={modalElement != null}>
      <Modal id={"oc-profile-preview-modal"} onClose={onClose} show={modalElement != null}>
        <DialogTitle
          onClose={onClose}
          iconProps={{ name: "xmark", dataTestId: `${PROFILE_TEST_PREFIX}-preview-close-button` }}
        >
          <Text size="small" modifier="bold">
            {appstr(modalElement?.type === ProfileTypes.Agent ? "Agent profile" : "Profile")}
          </Text>
        </DialogTitle>
        <DialogContent customContentClass="ocn-profile-card">
          {!modalElement?.record ? (
            <BigInfoIcon
              message={appstr(isHolder ? "No holders in preview" : "Preview not available")}
              name={isHolder ? "user-slash" : "triangle-exclamation"}
            />
          ) : modalElement?.type === ProfileTypes.Agent ? (
            <AgentBaseballCard
              record={modalElement.record}
              agentConfig={agentSettings.fields}
              includedSlotIds={agentSettings.shownSlots ?? null}
              availableSlotIds={agentAvailableSlots}
              // Preview opens expanded (state starts null) so admins see every section.
              showMoreFields={showMoreFields ?? true}
              setShowMoreFields={setShowMoreFields}
            />
          ) : isPositionElement ? (
            <PositionBaseballCard
              record={modalElement?.record}
              headingConfig={headingConfig}
              visibleFields={getVisibleFields(ProfileTypes.Position)}
              nAssignees={modalElement?.record?.["__HOLDERS__"]?.length || 0}
              showMoreFields={showMoreFields}
              setShowMoreFields={setShowMoreFields}
              mapping={chartConfig.fields ?? {}}
            />
          ) : (
            <EmployeeBaseballCard
              record={modalElement?.record}
              headingConfig={headingConfig}
              isPositionChart={isPositionChart}
              visibleFields={getVisibleFields(ProfileTypes.Employee)}
              mapping={chartConfig.fields ?? {}}
              favoriteController={favoriteOptions}
              includeScrollableArea
              showMoreFields={showMoreFields}
              setShowMoreFields={setShowMoreFields}
            />
          )}
        </DialogContent>
      </Modal>
    </CentralizedModalContainer>
  );
};
